/**
 * Reminders domain — cache invalidation registry.
 *
 * Every wrapped reminder mutation invalidates the shared list cache via
 * `commonReminderInvalidations()`. Detail-level mutations also flush
 * the per-id detail cache. The legacy `['reminders']` string key
 * remains in the common set until every consumer of `api.getReminders`
 * is migrated; this keeps both worlds in sync during migration.
 */

import type { QueryKey } from '@tanstack/react-query'

import { type InvalidationRule } from '../invalidation/useInvalidatingMutation'
import {
  getReminderDetailQueryKey,
  listRemindersQueryKey,
} from '../generated/hooks/remindersController'

export type RemindersMutationId =
  | 'createReminder'
  | 'updateReminder'
  | 'partialUpdateReminder'
  | 'deleteReminder'
  | 'pauseReminder'
  | 'unpauseReminder'

export function commonReminderInvalidations(): QueryKey[] {
  return [listRemindersQueryKey(), ['reminders']]
}

export const invalidationRegistry: Record<
  RemindersMutationId,
  InvalidationRule<any>
> = {
  createReminder: {
    invalidates: () => commonReminderInvalidations(),
  },
  updateReminder: {
    invalidates: (variables: { id: number }) => [
      ...commonReminderInvalidations(),
      getReminderDetailQueryKey({ id: variables.id }),
      ['reminder', variables.id],
    ],
  },
  partialUpdateReminder: {
    invalidates: (variables: { id: number }) => [
      ...commonReminderInvalidations(),
      getReminderDetailQueryKey({ id: variables.id }),
      ['reminder', variables.id],
    ],
  },
  deleteReminder: {
    invalidates: (variables: { id: number }) => [
      ...commonReminderInvalidations(),
      getReminderDetailQueryKey({ id: variables.id }),
      ['reminder', variables.id],
    ],
  },
  pauseReminder: {
    invalidates: (variables: { id: number }) => [
      ...commonReminderInvalidations(),
      getReminderDetailQueryKey({ id: variables.id }),
      ['reminder', variables.id],
    ],
  },
  unpauseReminder: {
    invalidates: (variables: { id: number }) => [
      ...commonReminderInvalidations(),
      getReminderDetailQueryKey({ id: variables.id }),
      ['reminder', variables.id],
    ],
  },
}
